import { saveRecentProduct } from '@/components/content/Header/utils/search';
import { classifyCombinablePromotions } from '@/components/blocks/FreeGiftV2/utils';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { usePromoMessage } from '@/data/Content/PromoMessage';
import { ProductType } from '@/data/types/Product';
import { CombinablePromotion } from '@/data/types/Promotions';
import { useUser } from '@/data/User';
import { usePathname } from 'next/navigation';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackItemDeliveryPDP } from 'presentation/events/hooks/useTrackItemDeliveryPDP';
import { useTrackPageLoad } from 'presentation/events/hooks/useTrackPageLoad';
import { useTrackPageView } from 'presentation/events/hooks/useTrackPageView';
import {
	EVENT_SEARCH_SUBMITTED_TERM_KEY,
	SEARCH_TYPE,
	decodeUrlDestination,
} from 'presentation/events/hooks/useTrackSearchSubmit';
import { useTrackSearchSubmitGA4 } from 'presentation/events/hooks/useTrackSearchSubmit/useTrackSearchSubmitGA4';
import { useTrackViewItem } from 'presentation/events/hooks/useTrackViewItem';
import { PersonInfoForTracking } from 'presentation/events/types/mediaTypes';
import { waitForReactToBeIdle } from 'presentation/events/utils/ga4Utils';
import { getGiftType } from 'presentation/events/utils/freeProductUtils';
import { createExternalIdValue } from 'presentation/events/utils/pinterestUtils';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom React hook that centralizes all tracking events triggered on PDP load.
 *
 * Each event (`page_view`, `view_item`, `page_load`) fires independently, in its own
 * effect, gated only by the data it actually needs:
 * - `page_view` needs user/personInfo, not the product.
 * - `view_item` needs the product and personInfo, not the user object.
 * - `page_load` needs the product and the page to actually finish rendering
 *   (`waitForReactToBeIdle`), not user/personInfo.
 *
 * @param product - The PDP product object, required for tracking `view_item` and `page_load` events.
 */
export const usePDPTrackLoadEvents = ({
	product,
	isStoreOnlyAvailable,
}: {
	product: ProductType | undefined;
	isStoreOnlyAvailable?: boolean;
}) => {
	const pathname = usePathname();
	const { breadcrumb } = useBreadcrumbTrail();

	const { physicalStoreId } = useCurrentPhysicalStoreId();

	const { user, loading: isUserLoading } = useUser();
	const { personInfo, isLoading: isPersonInfoLoading } = usePersonInfo();

	const userId = user?.userId ?? null;
	const isLoggedIn = !!user?.isLoggedIn;

	const { data: promoMessage, loading: isPromoMessageLoading } = usePromoMessage(product?.id ?? '');
	const combinablePromotions = promoMessage?.combinablePromotions as
		| CombinablePromotion[]
		| undefined;
	const promoThdCode = classifyCombinablePromotions(combinablePromotions);
	const giftType = getGiftType({
		partNumber: product ? getItemPartNumber(product) : undefined,
		combinablePromotions,
	});

	const trackPageView = useTrackPageView();
	const trackViewItem = useTrackViewItem();
	const trackPageLoad = useTrackPageLoad();
	const { sendGA4Event } = useTrackSearchSubmitGA4();

	useTrackItemDeliveryPDP({
		product: pathname?.startsWith('/p/') ? product : undefined,
		isStoreOnlyAvailable,
	});

	const personInfoRef = useRef(personInfo);
	personInfoRef.current = personInfo;
	const breadcrumbRef = useRef(breadcrumb);
	breadcrumbRef.current = breadcrumb;
	const promoThdCodeRef = useRef(promoThdCode);
	promoThdCodeRef.current = promoThdCode;
	const giftTypeRef = useRef(giftType);
	giftTypeRef.current = giftType;

	const pageViewTrackedKeyRef = useRef<string | null>(null);
	const viewItemTrackedKeyRef = useRef<string | null>(null);
	const pageLoadTrackedKeyRef = useRef<string | null>(null);

	const buildPersonInfoForTracking = useCallback((): PersonInfoForTracking => {
		const currentPersonInfo = personInfoRef.current;
		return {
			email: currentPersonInfo?.email1,
			firstName: currentPersonInfo?.firstName,
			lastName: currentPersonInfo?.lastName,
			phone: currentPersonInfo?.phone1,
			city: currentPersonInfo?.city,
			state: currentPersonInfo?.state,
			zipCode: currentPersonInfo?.zipCode,
		};
	}, []);

	useEffect(() => {
		if (!product || !pathname?.startsWith('/p/')) return;

		const submittedTerm = sessionStorage.getItem(EVENT_SEARCH_SUBMITTED_TERM_KEY);
		if (!submittedTerm) return;
		sessionStorage.removeItem(EVENT_SEARCH_SUBMITTED_TERM_KEY);

		const term = submittedTerm.trim();
		if (!term) return;

		sendGA4Event({
			searchTerm: term,
			searchType: SEARCH_TYPE.PRODUCT_REDIRECT,
			urlDestination: decodeUrlDestination(window.location.href),
			numberOfResults: -1,
		});
	}, [product, pathname, sendGA4Event]);

	useEffect(() => {
		if (!product) return;
		if (!pathname?.startsWith('/p/')) return;

		saveRecentProduct(product);
	}, [product, pathname]);

	// page_view: needs user/personInfo, not the product.
	useEffect(() => {
		if (!pathname?.startsWith('/p/')) return;
		if (isUserLoading || isPersonInfoLoading) return;

		const trackingKey = `${pathname}|${physicalStoreId}`;
		if (pageViewTrackedKeyRef.current === trackingKey) return;
		pageViewTrackedKeyRef.current = trackingKey;

		createExternalIdValue();

		trackPageView({
			userId,
			isLoggedIn,
			personInfo: buildPersonInfoForTracking(),
		});
	}, [
		pathname,
		physicalStoreId,
		userId,
		isLoggedIn,
		isUserLoading,
		isPersonInfoLoading,
		trackPageView,
		buildPersonInfoForTracking,
	]);

	// view_item: needs the product and personInfo, not the user object.
	useEffect(() => {
		if (!product) return;
		if (!pathname?.startsWith('/p/')) return;
		if (isPersonInfoLoading) return;
		if (isPromoMessageLoading) return;

		const trackingKey = `${pathname}|${physicalStoreId}`;
		if (viewItemTrackedKeyRef.current === trackingKey) return;
		viewItemTrackedKeyRef.current = trackingKey;

		trackViewItem({
			product: {
				...product,
				promoThdCode: promoThdCodeRef.current,
				giftType: giftTypeRef.current,
			},
			personInfo: buildPersonInfoForTracking(),
		});
	}, [
		product,
		pathname,
		physicalStoreId,
		isPersonInfoLoading,
		isPromoMessageLoading,
		trackViewItem,
		buildPersonInfoForTracking,
	]);

	// page_load: needs the product and the real page load (waitForReactToBeIdle), not user/personInfo.
	useEffect(() => {
		if (!product) return;
		if (!pathname?.startsWith('/p/')) return;

		const trackingKey = `${pathname}|${physicalStoreId}`;
		if (pageLoadTrackedKeyRef.current === trackingKey) return;

		let isMounted = true;

		const categories =
			breadcrumbRef.current?.filter((cat) => cat.type === undefined).map((cat) => cat.label) ?? [];

		waitForReactToBeIdle().then(() => {
			if (!isMounted) return;
			if (pageLoadTrackedKeyRef.current === trackingKey) return;

			pageLoadTrackedKeyRef.current = trackingKey;

			trackPageLoad({
				categories,
				sku: product.partNumber,
			});
		});

		return () => {
			isMounted = false;
		};
	}, [product, pathname, physicalStoreId, trackPageLoad]);
};
